/**
 * Завантажувач конфігурації для Kernel
 * Завантажує конфігурацію додатку з різних джерел
 */

import { IAppConfig } from '../../Contracts/Configs/IAppConfig'
import { IConfigLoader } from '../../Contracts/Loaders/IConfigLoader'
import { configManager } from '../../Managers/Config'
import { BaseLoader } from './BaseLoader'

export class ConfigKernelLoader extends BaseLoader {
    private configs: Partial<IAppConfig> = {}
    private configVersion: string = '1.0.0'

    constructor() {
        const loaderConfig: IConfigLoader = {
            name: 'config',
            priority: 15,
            clientSafe: true,
            dependencies: [],
        }
        super(loaderConfig)
    }

    async load(): Promise<void> {
        try {
            this.log('Starting config loading...')
            await this.loadWithRetry(async () => {
                await this.loadUserConfig()
                this.populateConfigManager()
            })
            this.enableHotReload()
            this.log('Config loading completed successfully')
        } catch (error) {
            this.log('Error loading config, using fallback: ' + error, 'error')
        }
    }

    /**
     * Отримує всі завантажені конфігурації
     */
    public getConfigs(): Partial<IAppConfig> {
        return { ...this.configs }
    }

    /**
     * Отримує статистику завантаження конфігурацій
     */
    public getConfigStats() {
        const configKeys = Object.keys(this.configs)
        const systemKeys = ['app', 'api', 'features', 'theme', 'locale']
        const userKeys = configKeys.filter((key) => !systemKeys.includes(key))

        return {
            totalConfigs: configKeys.length,
            systemConfigs: systemKeys.filter((key) => key in this.configs && this.configs[key as keyof IAppConfig])
                .length,
            userConfigs: userKeys.length,
            environment: (this.configs as any).app?.environment || 'unknown',
            configKeys: configKeys,
            systemKeys: systemKeys.filter((key) => key in this.configs && this.configs[key as keyof IAppConfig]),
            userKeys: userKeys,
            version: this.configVersion,
        }
    }

    /**
     * Валідація конфігурації
     */
    private validateConfig(config: any): boolean {
        if (!config || typeof config !== 'object') {
            this.log('Invalid config: not an object', 'warn')
            return false
        }
        return true
    }

    /**
     * Hot reload підтримка для development режиму
     */
    private enableHotReload(): void {
        if (!this.isClient()) return
        if (import.meta.env?.MODE !== 'development') return

        if (import.meta.hot) {
            this.log('Hot reload enabled for configs')
            import.meta.hot.accept('../../../../.react-router/generated-configs', (newModule) => {
                if (newModule?.['generatedConfigs']) {
                    this.log('Hot reloading configs...', 'info')
                    this.refresh(newModule['generatedConfigs'])
                }
            })
        }
    }

    /**
     * Оновлення конфігурацій (для hot reload)
     */
    public refresh(newConfigs: Partial<IAppConfig>): void {
        this.clearCache()
        this.configs = { ...newConfigs }
        this.populateConfigManager()
        this.log('Configs refreshed', 'info')
    }

    private async loadUserConfig(): Promise<void> {
        if (!this.isClient()) return

        try {
            // В production та development режимах використовуємо статичний імпорт
            // Vite автоматично оптимізує це під час збірки
            // @ts-ignore - файл генерується під час збірки
            const module = await import('../../../../.react-router/generated-configs')
            const { generatedConfigs } = module

            if (generatedConfigs && this.validateConfig(generatedConfigs)) {
                // Додаємо всі конфігурації до стору
                for (const [key, value] of Object.entries(generatedConfigs)) {
                    if (key && value && this.validateConfig(value)) {
                        this.configs = { ...this.configs, [key]: value }
                    }
                }
                this.log(`Loaded ${Object.keys(this.configs).length} configs`)
            }
        } catch (error) {
            this.log('Failed to load generated configs: ' + error, 'error')
        }
    }

    /**
     * Заповнює глобальний ConfigManager всіма завантаженими конфігураціями
     */
    private populateConfigManager(): void {
        // Очищуємо попередні конфігурації
        configManager.clear()

        // Передаємо всі завантажені конфігурації в ConfigManager
        configManager.sets(this.configs)
        this.log('ConfigManager populated with configs')
    }
}
